import { useRef, useEffect } from 'react';
import type { Message } from '@unburden/common';
import { formatDistanceToNow } from 'date-fns';
import { authedUrl } from '../api';

function isImage(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

interface MessageViewerProps {
  messages: Message[];
  loading: boolean;
}

export function MessageViewer({ messages, loading }: MessageViewerProps) {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-gray-500">Loading messages...</p>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-gray-500">No messages to display</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4 space-y-4">
      {messages.map((message) => (
        <div key={message.id} className="flex flex-col">
          <div className="flex items-baseline space-x-2">
            <span className="text-sm font-medium text-gray-900">{message.sender}</span>
            <span className="text-xs text-gray-500">
              {formatDistanceToNow(message.timestamp * 1000, { addSuffix: true })}
            </span>
          </div>
          <div className="mt-1 p-3 bg-gray-100 rounded-lg text-sm text-gray-800 break-words">
            {message.content}
          </div>
          {message.attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {message.attachments.map((attachment) =>
                isImage(attachment.mimeType) ? (
                  <a
                    key={attachment.id}
                    href={authedUrl(`/api/attachments/${attachment.id}`)}
                    target="_blank"
                    rel="noreferrer"
                    title={attachment.filename}
                  >
                    <img
                      src={authedUrl(`/api/attachments/${attachment.id}`)}
                      alt={attachment.filename}
                      loading="lazy"
                      className="max-h-48 max-w-xs rounded border border-gray-200 object-contain"
                    />
                  </a>
                ) : (
                  <a
                    key={attachment.id}
                    href={authedUrl(`/api/attachments/${attachment.id}`)}
                    target="_blank"
                    rel="noreferrer"
                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded hover:bg-blue-200"
                  >
                    📎 {attachment.filename}
                  </a>
                )
              )}
            </div>
          )}
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
}
